import { useRef, useState } from 'react'
import { useAuthContext } from '../../context/auth.context'
import { useApiFormLaunch } from '../../api-new/state/auth'
import { serverMessage } from '../error/server-message'

// Opens the web viewer for the caller's own session, in a new tab. Shared by
// a course's "Launch" (with the course as the target) and the staff
// sidebar's "Web viewer" (no target: the viewer's home).
//
// The viewer signs in from a one-time launch code, so this mints one with
// auth's POST /api/v1/launch, for this session, server-side, and sends the
// tab to the URL the server returns: the admin's token never enters a URL,
// and the viewer's address is the server's (VIEWER_PUBLIC_ORIGIN), never
// the admin's. Open to every signed-in user since 2026-10-08.
//
// The tab is opened synchronously inside the click: a popup blocker allows a
// window only during the user gesture (Safari's lasts about a second), and
// the mint can take longer. It is detached from this page (opener = null,
// what noopener would do -- noopener itself makes window.open return null,
// leaving no tab to point anywhere), and only then sent to the launch URL. A
// failed mint closes it. So `launch` must be called straight from a click
// handler, before any await.
//
// One launch at a time: every code is a session hand-off, so a double click
// must not mint two. The ref closes the window before the re-render that
// shows `launching` lands.
//
// `failure` is { message, error }: the line to show and the API error behind
// it, for <ErrorReference error={failure.error} /> (null when there is no
// request, e.g. a blocked popup).

export const VIEWER_BLOCKED_MESSAGE = 'Your browser blocked the new tab. Allow pop-ups for this site and try again.'
export const VIEWER_FAILED_MESSAGE = "Couldn't open the web viewer. Please try again."

export const useViewerLaunch = () => {
  const { state: auth } = useAuthContext()
  const apiFormLaunch = useApiFormLaunch()

  const inFlight = useRef(false)
  const [launching, setLaunching] = useState(false)
  const [failure, setFailure] = useState(null)

  // Resolves true once the tab is on its way to the viewer, false otherwise.
  const launch = async target => {
    if (inFlight.current) return false
    setFailure(null)

    const tab = window.open('', '_blank')
    if (!tab) {
      setFailure({ message: VIEWER_BLOCKED_MESSAGE, error: null })
      return false
    }
    tab.opener = null

    inFlight.current = true
    setLaunching(true)
    try {
      const request = { user: auth.loginID, org: auth.organizationID }
      if (target) request.target = target

      const result = await apiFormLaunch.post(request)
      if (!result || typeof result.url !== 'string' || !result.url) throw new Error('The launch answer has no URL.')

      tab.location.replace(result.url)
      return true
    } catch (e) {
      tab.close()
      setFailure({ message: serverMessage(e, VIEWER_FAILED_MESSAGE), error: e })
      return false
    } finally {
      inFlight.current = false
      setLaunching(false)
    }
  }

  const dismiss = () => setFailure(null)

  return { launch, launching, failure, dismiss }
}
